import {
	Directive,
	DoCheck,
	ElementRef,
	HostListener,
	InjectionToken,
	OnDestroy,
	OnInit,
	Renderer2,
	inject
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';

/**
 * Whether this browser paints an empty date field in a way that needs replacing.
 *
 * There is no feature to detect: the difference is in how the user agent paints an empty field, which
 * nothing exposes. Vendor rather than the user agent string, because Chrome and Edge on macOS carry
 * "Safari" in theirs but report Google as the vendor.
 *
 * A token rather than a call inside the directive so a test can render either browser's behaviour.
 */
export const DATE_PLACEHOLDER_REQUIRED = new InjectionToken<boolean>('DATE_PLACEHOLDER_REQUIRED', {
	providedIn: 'root',
	factory: () => typeof navigator !== 'undefined'
		&& /apple/i.test(navigator.vendor)
		&& !/chrome|chromium|crios|android/i.test(navigator.userAgent)
});

/**
 * Gives an empty date field the same mm/dd/yyyy hint in Safari that it already has elsewhere.
 *
 * Safari paints today's date into the parts of an empty input that have no value yet, so a field
 * nobody has filled in reads as though someone had — and on a filter that silently suggests a range
 * is applied. The `placeholder` attribute cannot fix it: it is ignored on a date input, because the
 * empty text is drawn by the browser rather than by the page.
 *
 * So the native text is hidden and our own is drawn over it. Only in Safari — Chrome and Firefox
 * already paint mm/dd/yyyy, and replacing text that is already right could only move it.
 */
@Directive({
	selector: 'input[type="date"]',
	standalone: true
})
export class DatePlaceholderDirective implements OnInit, DoCheck, OnDestroy {

	public static readonly PLACEHOLDER_TEXT = 'mm/dd/yyyy';
	public static readonly HOST_CLASS = 'date-placeholder-host';
	public static readonly HINT_CLASS = 'date-placeholder';
	private static readonly EMPTY_CLASS = 'is-empty';

	private elementRef = inject<ElementRef<HTMLInputElement>>(ElementRef);
	private renderer = inject(Renderer2);
	private isRequired = inject(DATE_PLACEHOLDER_REQUIRED);
	/** Present for ngModel and formControlName, which is how every date input here is bound. */
	private ngControl = inject(NgControl, { optional: true, self: true });

	private originalParent?: Node;
	private wrapper?: HTMLElement;
	private valueChanges?: Subscription;
	/** What the field last reported, so a check that changes nothing also touches nothing. */
	private wasEmpty?: boolean;

	public ngOnInit(): void {
		// Nothing is built at all in the browsers that are already correct, so they cannot regress.
		if (!this.isRequired) return;

		this.build();
		this.sync();

		// A date set from code — a filter reset, a form patch — changes no event the host would hear.
		this.valueChanges = this.ngControl?.valueChanges?.subscribe(() => this.sync());
	}

	/**
	 * A [value] binding writes the property without raising an event and without a control to watch —
	 * which is how the match panel clears its date range. There is nothing to subscribe to, so the
	 * field is read on each check of the view that owns it. The guard in sync() keeps that to a string
	 * comparison until the answer actually changes.
	 */
	public ngDoCheck(): void {
		this.sync();
	}

	public ngOnDestroy(): void {
		this.valueChanges?.unsubscribe();
		this.unwrap();
	}

	/** Typing and picking both settle on the input itself. */
	@HostListener('input')
	@HostListener('change')
	public onValueChanged(): void {
		this.sync();
	}

	//#region Private Methods

	private build(): void {
		const input = this.elementRef.nativeElement;
		const parent = this.renderer.parentNode(input) as Node | null;
		if (!parent) return;

		this.originalParent = parent;

		// Wrapped rather than given a sibling, so the hint is positioned against the field itself and
		// stays put whatever else shares the field's container — a label above it, most often.
		const wrapper = this.renderer.createElement('span') as HTMLElement;
		this.renderer.addClass(wrapper, DatePlaceholderDirective.HOST_CLASS);
		this.renderer.insertBefore(parent, wrapper, input);
		this.renderer.appendChild(wrapper, input);

		const hint = this.renderer.createElement('span') as HTMLElement;
		this.renderer.addClass(hint, DatePlaceholderDirective.HINT_CLASS);
		// Decoration, not content: the field carries its own label, and a screen reader announcing
		// "mm/dd/yyyy" beside it would be reading the format out as though it were a value.
		this.renderer.setAttribute(hint, 'aria-hidden', 'true');
		this.renderer.appendChild(hint, this.renderer.createText(DatePlaceholderDirective.PLACEHOLDER_TEXT));
		this.renderer.appendChild(wrapper, hint);

		this.wrapper = wrapper;
		this.alignToField(input, hint);
	}

	/**
	 * Takes the hint's type and inset from the field it covers rather than restating .form-control's
	 * padding, which form-control-sm changes and a restated figure would not follow.
	 */
	private alignToField(input: HTMLInputElement, hint: HTMLElement): void {
		const field = getComputedStyle(input);

		for (const [property, value] of [
			['left', field.paddingLeft],
			['font-size', field.fontSize],
			['font-family', field.fontFamily],
			['font-weight', field.fontWeight]
		]) {
			this.renderer.setStyle(hint, property, value);
		}
	}

	private sync(): void {
		if (!this.wrapper) return;

		const isEmpty = !this.elementRef.nativeElement.value;
		if (isEmpty === this.wasEmpty) return;

		this.wasEmpty = isEmpty;
		const action = isEmpty ? 'addClass' : 'removeClass';
		this.renderer[action](this.wrapper, DatePlaceholderDirective.EMPTY_CLASS);
	}

	/**
	 * Puts the field back where it was found before the wrapper goes. Angular removes the input on its
	 * own when the view is destroyed, and it would otherwise be looking inside a node we had removed.
	 */
	private unwrap(): void {
		const input = this.elementRef.nativeElement;
		if (!this.wrapper || !this.originalParent) return;

		this.renderer.insertBefore(this.originalParent, input, this.wrapper);
		this.renderer.removeChild(this.originalParent, this.wrapper);

		this.wrapper = undefined;
		this.originalParent = undefined;
	}

	//#endregion Private Methods
}
